/* Offline recitations. The audio goes into the same Cache Storage bucket the
   service worker serves media from (AUDIO_CACHE in public/sw.js), keyed by the
   exact audio URL, so playback finds it with no extra wiring. Timings are
   cached via the regular /api/qf request (DATA_CACHE). */
import type { Reciter } from '@/data/reciters';
import { loadTrack } from './timings';

const AUDIO_CACHE = 'ftu-audio-v1';

async function urlFor(r: Reciter, surah: number) {
  return (await loadTrack(r, surah)).url;
}

export async function isDownloaded(r: Reciter, surah: number): Promise<boolean> {
  if (!('caches' in window) || r.audio === 'juz') return false;
  try {
    const c = await caches.open(AUDIO_CACHE);
    // Template reciters have a deterministic URL; qf ones need the API (cached) to resolve.
    const url = r.audio === 'template' ? r.url.replace('{sss}', String(surah).padStart(3, '0')) : await urlFor(r, surah);
    return !!(await c.match(url, { ignoreSearch: true }));
  } catch { return false; }
}

export async function downloadSurah(r: Reciter, surah: number, onProgress?: (pct: number) => void) {
  const url = await urlFor(r, surah);
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok || !res.body) throw new Error(`Download failed (${res.status})`);
  const total = Number(res.headers.get('content-length')) || 0;
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let got = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    got += value.length;
    if (total) onProgress?.(Math.round((got / total) * 100));
  }
  const blob = new Blob(chunks as BlobPart[], { type: res.headers.get('content-type') || 'audio/mpeg' });
  const c = await caches.open(AUDIO_CACHE);
  await c.put(url, new Response(blob, { headers: { 'Content-Type': blob.type, 'Content-Length': String(blob.size) } }));
  // Ask the browser not to evict downloads under storage pressure.
  try { await navigator.storage?.persist?.(); } catch {}
}

export async function removeDownload(r: Reciter, surah: number) {
  const c = await caches.open(AUDIO_CACHE);
  await c.delete(await urlFor(r, surah), { ignoreSearch: true });
}

export async function storageSummary() {
  const est = await navigator.storage?.estimate?.();
  let audioCount = 0;
  try { audioCount = (await (await caches.open(AUDIO_CACHE)).keys()).length; } catch {}
  return { used: est?.usage ?? 0, quota: est?.quota ?? 0, audioCount };
}

export async function clearCache(name: 'audio' | 'data' | 'fonts') {
  await caches.delete({ audio: AUDIO_CACHE, data: 'ftu-data-v1', fonts: 'ftu-fonts-v1' }[name]);
}
